import { useState } from "react";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { ProjectTemplate } from "../config/projectTemplates";

/**
 * Hook to apply a starter template to a newly created project.
 * Writes pre-seeded step data (steps 1-10) to Firestore progress sub-collection.
 */
export function useProjectTemplate() {
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyTemplate = async (
    userID: string,
    projectID: string,
    template: ProjectTemplate
  ): Promise<boolean> => {
    setIsApplying(true);
    setError(null);

    try {
      const writes = Object.entries(template.steps).map(([stepKey, formData]) => {
        const stepRef = doc(
          db,
          "users",
          userID,
          "projects",
          projectID,
          "progress",
          stepKey
        );
        return setDoc(stepRef, {
          form: formData,
          isTemplate: true,
          templateId: template.id,
          updatedAt: new Date().toISOString(),
        });
      });

      await Promise.all(writes);

      // Update project doc to note template was used
      const projectRef = doc(db, "users", userID, "projects", projectID);
      await updateDoc(projectRef, {
        templateId: template.id,
        templateName: template.name,
        projectTitle: template.name,
        projectDescription: template.description,
      });

      return true;
    } catch (err) {
      console.error("Template application failed:", err);
      setError("Failed to apply template. You can continue and fill in the steps manually.");
      return false;
    } finally {
      setIsApplying(false);
    }
  };

  return { applyTemplate, isApplying, error };
}
